import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Font } from '@react-pdf/renderer';
import { styled } from '@mui/material/styles';
import { Box, Divider, Drawer, Grid, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import { appURL } from '../Data/baseURL';
import base from '../Data/MetaData/base.json';
import { ContDePlataLogo_onwhite1 } from '../Data/imageURL';
import MetaDecorator from '../Apm/Other/MetaDecorator';
import HeaderDrawerList from './HeaderDrawerList';
import Navbar from './NavBar';
import { appChromePalette, publicPalette } from '../Public/PublicTheme';

Font.register({
  family: 'Oswald',
  src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
});

const drawerWidth = 265;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('md')]: { width: `calc(${theme.spacing(8)} + 1px)` },
  [theme.breakpoints.down('md')]: { width: 0 },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  minHeight: 72,
  padding: theme.spacing(0, 1),
}));

const PUBLIC_EXACT_PATHS = new Set([
  '/',
  `${appURL}/login`,
  `${appURL}/signup`,
  `${appURL}/passreset`,
  `${appURL}/terms-conditions`,
  `${appURL}/faq`,
  `${appURL}/support`,
  `${appURL}/products`,
  '/not-found',
]);

const isPublicPath = (pathname) =>
  PUBLIC_EXACT_PATHS.has(pathname) ||
  pathname.startsWith('/api/password/reset/confirm/') ||
  pathname.startsWith('/api/activate/');

export default function Header({ children }) {
  const { pathname } = useLocation();
  const theme = useTheme();
  const lgMediaDown = useMediaQuery(theme.breakpoints.down('lg'));
  const [open, setOpen] = React.useState(false);
  const { isAuthenticated, isAuthLoading } = useSelector((state) => state.authSlice);
  const publicRoute = isPublicPath(pathname);

  useEffect(() => {
    const loadFonts = async () => {
      try {
        await Promise.all([
          Font.load({ fontFamily: 'Roboto' }),
          Font.load({ fontFamily: 'Oswald' }),
        ]);
      } catch (error) {
        console.error('PDF fonts could not be loaded:', error);
      }
    };

    void loadFonts();
  }, []);

  useEffect(() => {
    if (lgMediaDown) {
      setOpen(false);
    }
  }, [pathname, lgMediaDown]);

  useEffect(() => {
    setOpen(false);
  }, [isAuthenticated]);

  const showDrawer = !isAuthLoading && (publicRoute ? lgMediaDown : isAuthenticated);
  const handleDrawerClose = () => setOpen(false);

  return (
    <>
      <MetaDecorator
        description={base.pageDescriptionRo}
        title={base.pageTitle}
        imageUrl={ContDePlataLogo_onwhite1}
        imageAlt={base.pageDescriptionRo}
        rel="https://contdeplata.md/"
      />

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          minWidth: 0,
          minHeight: '100vh',
          backgroundColor: publicRoute ? publicPalette.background : 'background.default',
        }}
      >
        <Navbar open={open} setOpen={setOpen} publicRoute={publicRoute} />

        {showDrawer && (
          <Drawer
            variant={lgMediaDown ? 'temporary' : 'permanent'}
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              whiteSpace: 'nowrap',
              boxSizing: 'border-box',
              ...(open && {
                ...openedMixin(theme),
                '& .MuiDrawer-paper': openedMixin(theme),
              }),
              ...(!open && {
                ...closedMixin(theme),
                '& .MuiDrawer-paper': closedMixin(theme),
              }),
            }}
            PaperProps={{
              sx: {
                color: 'white',
                backgroundColor: publicRoute ? '#07101F' : appChromePalette.drawer,
                borderRight: `1px solid ${publicRoute ? publicPalette.border : appChromePalette.border}`,
                boxShadow: publicRoute ? 'none' : '12px 0 32px rgba(2, 6, 23, 0.16)',
              },
            }}
            anchor="left"
            open={open}
            onClose={handleDrawerClose}
          >
            <DrawerHeader>
              <IconButton onClick={handleDrawerClose} color="inherit" aria-label="Close navigation">
                <ChevronLeftIcon />
              </IconButton>
            </DrawerHeader>
            {isAuthenticated && <Divider sx={{ borderColor: appChromePalette.border }} />}
            <HeaderDrawerList
              isAuthenticated={isAuthenticated}
              publicRoute={publicRoute}
              open={open}
              handleDrawerClose={handleDrawerClose}
            />
          </Drawer>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            minHeight: '100vh',
            overflowX: 'clip',
            overflowY: 'visible',
            backgroundColor: publicRoute ? publicPalette.background : 'background.default',
          }}
        >
          <Box sx={{ minHeight: publicRoute ? { xs: 68, md: 76 } : 56 }} />

          {publicRoute ? (
            children
          ) : (
            <Box sx={{ width: '100%', px: { xs: 1, sm: 2, xl: 4 }, pt: 2, pb: 1 }}>
              <Grid container sx={{ width: '100%', minWidth: 0 }}>
                {children}
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
